// Which auctions the phone keeps offline, and how often it polls (BL-2344, ADR 066 — Offline
// Mode § 4.5 and § 5). One pure rule, shared by the server's delta and the phone's sync engine.
import type { AuctionPhaseKind } from '@auctogon/core/isomorphic/lifecycle';

/**
 * The phases an auction stops syncing in. Accepting bids still syncs (operator, 2026-10-08):
 * an auction drops off the device only once it is Closed, or deleted.
 */
export const OFFLINE_SYNC_EXCLUDED_PHASES: readonly AuctionPhaseKind[] = ['closed', 'deleted'];

/** How often the phone polls while the app is in the foreground and online (ADR 066 § 5). */
export const OFFLINE_SYNC_POLL_MS = 5 * 60 * 1000;

/**
 * Whether an auction in this phase belongs on the device.
 *
 * @param phase - the auction's derived phase.
 * @returns true for Draft, Ready, Accepting pre-bids, Accepting bids and Paused.
 */
export function isOfflineSyncEligible(phase: AuctionPhaseKind): boolean {
  return !OFFLINE_SYNC_EXCLUDED_PHASES.includes(phase);
}

/**
 * Whether the device syncs an auction: an eligible auction syncs unless someone turned its
 * switch off on this device. No recorded choice means on (new auctions default to on).
 *
 * @param input.phase - the auction's derived phase.
 * @param input.setting - this device's recorded choice for the auction, if any.
 * @returns true when the auction should be on the device.
 */
export function isOfflineSyncEnabled(input: {
  phase: AuctionPhaseKind;
  setting: { enabled: boolean } | null | undefined;
}): boolean {
  if (!isOfflineSyncEligible(input.phase)) return false;
  return input.setting?.enabled ?? true;
}
